// Converting a middlegame advantage (`GameFacts.conversion`): whether the
// reader stood winning (the rules' `winningPawns`, the standing Middlegame
// results calls winning) before one of their middlegame moves, from which
// move, and — when the game was not won — where the lead went: a loss on
// time is `time`; otherwise the cause that cost the most win chance from
// that move to the end of the game, by `@chess/api-mistakes`'
// `lostGroundCause` (the rule of Moves to practise) over the reader's
// counted mistakes (`mistakes.ts`, which runs first), the ending's included.
// Unlike the middlegame result, which reads the stage's first and last
// positions only, a lead won and lost inside the middlegame counts.
import type { GameFacts } from '@chess/api-model';
import { lostGroundCause, patternOf, type GroundMove } from '@chess/api-mistakes';
import type { FactExtractor, ScannedGame } from '../extractor.ts';

export const conversion: FactExtractor = {
  id: 'conversion',
  extract(game: ScannedGame, facts: GameFacts): void {
    const { fromPly, toPly } = game.range('middlegame');
    let from: number | undefined;
    for (let ply = fromPly + 1; ply <= toPly && from === undefined; ply++) {
      if (!game.isReaders(ply)) continue;
      const before = game.pawnsAt(ply - 1);
      if (before !== undefined && before >= game.rules.winningPawns) from = ply;
    }
    if (from === undefined) return;
    if (facts.outcome === 'win') {
      facts.conversion = { from };
      return;
    }
    const moves: GroundMove[] = (facts.mistakes ?? [])
      .filter((m) => m.ply >= from)
      .map((m) => ({
        ply: m.ply,
        san: m.san,
        kind: m.kind,
        before: m.before,
        after: m.after,
        pattern: patternOf(m, game.reader).pattern,
        clockSeconds: game.clockAfter(m.ply),
      }));
    const found = lostGroundCause(moves, game.reader, game.rules.winningPawns);
    if (facts.outcome === 'loss' && facts.ending === 'time') {
      const timeMove = found && found.costs.time > 0 ? practiceOf(moves, 'time', game) : undefined;
      facts.conversion = { from, cause: 'time', ...(timeMove ? { practice: timeMove } : {}) };
      return;
    }
    facts.conversion = found ? { from, cause: found.cause, practice: found.practice } : { from };
  },
};

/** The costliest of the moves whose cause is `time`: the one to practise for a flag fall that also had one. */
function practiceOf(moves: GroundMove[], cause: 'time', game: ScannedGame): { ply: number; san: string } | undefined {
  const own = moves.filter((m) => game.isReaders(m.ply));
  const timed = own.filter((m) => lostGroundCause([m], game.reader, game.rules.winningPawns)?.cause === cause);
  if (!timed.length) return undefined;
  return lostGroundCause(timed, game.reader, game.rules.winningPawns)?.practice;
}
